/* pp-geo.js — PDP geo personalization foundation (Phase 0, 2026-08-26).
 * Plan: pippy/_context/GEO_PERSONALIZATION_PLAN.md.
 *
 * Exposes window.ppGeo, a Promise resolving to:
 *   { country, region, region_name, city, conf, proof, reviews, dest }
 * conf is "city" | "region" | "country" | "none" — consumers must render
 * at whatever level is confident and KEEP their server-rendered fallback
 * below that (fail silent, never fail wrong — plan §2 rail #5).
 *
 * Resolution order: localStorage cache (24h) → GET https://pippy.co/geo
 * (non-blocking, hard timeout) → Liquid country fallback from the inline
 * #pp-geo-config JSON. Never blocks render, never reflows layout by
 * itself — it only swaps TEXT into opt-in [data-pp-geo-tpl] elements.
 *
 * dest = the shopper's EXPLICITLY CHOSEN destination (Phase 1 picker,
 * localStorage pp_dest). A choice always outranks detected geo — detected
 * is a default, never an override. Phase 0 only reads it.
 *
 * Declarative hydration contract (makes later features a copy edit):
 *   <span data-pp-geo-tpl="Delivered to {city} homes"
 *         data-pp-geo-min="city">…server-rendered fallback…</span>
 *   Tokens: {city} {region} {region_name} {country} {proof_count}
 *           {proof_label}
 *   data-pp-geo-min: minimum confidence to swap (default "city").
 *   data-pp-geo-need-proof="1": also require a proof line to exist.
 * After hydration a "pp:geo" CustomEvent fires on document with the geo
 * detail, for imperative consumers (Phase 1+).
 *
 * The visitor's timezone deliberately comes from the browser
 * (Intl.DateTimeFormat().resolvedOptions().timeZone), not from the IP —
 * it's both more accurate and free. Exposed as geo.tz.
 */
(function () {
  'use strict';
  if (window.ppGeo) return;                      // idempotent across surfaces

  var cfgEl = document.getElementById('pp-geo-config');
  var CFG = {};
  try { CFG = JSON.parse(cfgEl ? cfgEl.textContent : '{}') || {}; } catch (e) { CFG = {}; }
  /* ?v=2 busts the browser HTTP caches that pinned the pre-mmdb conf:none
     response during the 2026-08-25 launch window (it carried max-age=21600
     before the server learned to no-store bad answers). A different URL is
     a different cache key, so every browser refetches once. */
  var API = CFG.endpoint || 'https://pippy.co/geo?v=2';
  var TTL_MS = 24 * 3600 * 1000;
  var LEVELS = { none: 0, country: 1, region: 2, city: 3 };

  function browserTz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
    catch (e) { return ''; }
  }
  function readCache() {
    try {
      var raw = localStorage.getItem('pp_geo');
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.t || (Date.now() - o.t) > TTL_MS) return null;
      return o.v || null;
    } catch (e) { return null; }
  }
  function writeCache(v) {
    try { localStorage.setItem('pp_geo', JSON.stringify({ t: Date.now(), v: v })); }
    catch (e) { /* private mode etc — cache is optional */ }
  }
  function readDest() {
    try { return JSON.parse(localStorage.getItem('pp_dest') || 'null'); }
    catch (e) { return null; }
  }
  function fallback() {
    return {
      country: (CFG.country || '').toUpperCase(), region: '', region_name: '',
      city: '', conf: CFG.country ? 'country' : 'none', proof: null, reviews: []
    };
  }
  function fetchGeo() {
    return new Promise(function (resolve) {
      var done = false;
      var timer = setTimeout(function () {
        if (!done) { done = true; resolve(null); }
      }, CFG.timeout_ms || 6000);
      /* 6s, not 2.5s: the endpoint answers in ~200ms but during initial
         load this fetch queues behind the PDP's heavy third-party
         waterfall and 2.5s expired before it ever left the browser
         (measured 2026-08-26 on petalpal.com.au). Nothing blocks on it —
         the fallback line is already rendered and the label upgrades
         whenever this resolves. */
      try {
        fetch(API, { mode: 'cors' })
          .then(function (r) { return r && r.ok ? r.json() : null; })
          .then(function (j) {
            if (done) return;
            done = true; clearTimeout(timer);
            resolve(j && j.ok && j.conf && j.conf !== 'none' ? j : null);
          })
          .catch(function () {
            if (!done) { done = true; clearTimeout(timer); resolve(null); }
          });
      } catch (e) {
        if (!done) { done = true; clearTimeout(timer); resolve(null); }
      }
    });
  }
  function finish(geo) {
    geo = geo || fallback();
    geo.dest = readDest();
    geo.tz = browserTz();
    return geo;
  }

  window.ppGeo = new Promise(function (resolve) {
    var cached = readCache();
    if (cached) { resolve(finish(cached)); return; }
    var go = function () {
      fetchGeo().then(function (j) {
        if (j) { writeCache(j); resolve(finish(j)); }
        else { resolve(finish(null)); }
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', go);
    } else { go(); }
  });

  // Declarative hydration + the pp:geo event.
  window.ppGeo.then(function (geo) {
    try {
      var lvl = LEVELS[geo.conf] || 0;
      var proof = geo.proof || null;
      var els = document.querySelectorAll('[data-pp-geo-tpl]');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var min = LEVELS[el.getAttribute('data-pp-geo-min') || 'city'] || 3;
        if (lvl < min) continue;
        if (el.getAttribute('data-pp-geo-need-proof') === '1' &&
            !(proof && proof.count_label)) continue;
        el.textContent = el.getAttribute('data-pp-geo-tpl')
          .replace(/\{city\}/g, geo.city || '')
          .replace(/\{region\}/g, geo.region || '')
          .replace(/\{region_name\}/g, geo.region_name || '')
          .replace(/\{country\}/g, geo.country || '')
          .replace(/\{proof_count\}/g, proof ? proof.count_label : '')
          .replace(/\{proof_label\}/g, proof ? proof.label : '');
        /* Elements shipped [hidden] reveal only once their template
           resolved — a proof band must never show with empty tokens. */
        el.removeAttribute('hidden');
      }
    } catch (e) { /* hydration is an enhancement; never break the page */ }
    /* Local review cards (geo Phase 3b): fills #pp-local-reviews with 3
       curated same-market reviews from the /geo payload. Reviews are
       CUSTOMER TEXT — built with createElement/textContent only, never
       innerHTML. Fewer than 3 available → container stays hidden and the
       page is unchanged. */
    try {
      var host = document.getElementById('pp-local-reviews');
      var revs = geo.reviews || [];
      if (host && !host.childNodes.length && revs.length >= 3) {
        for (var r = 0; r < 3; r++) {
          var rv = revs[r] || {};
          var card = document.createElement('div');
          card.style.cssText = 'border:1px solid #e8e8e3;border-radius:12px;'
            + 'padding:14px 16px;background:#fff;';
          var stars = document.createElement('div');
          stars.style.cssText = 'color:#4a7220;font-size:0.95em;letter-spacing:2px;';
          var n = Math.max(1, Math.min(5, rv.rating || 5));
          stars.textContent = new Array(n + 1).join('★');
          var body = document.createElement('p');
          body.style.cssText = 'margin:8px 0 10px;line-height:1.5;display:-webkit-box;'
            + '-webkit-line-clamp:4;-webkit-box-orient:vertical;overflow:hidden;';
          body.textContent = rv.body || '';
          var who = document.createElement('div');
          who.style.cssText = 'font-weight:700;font-size:0.92em;';
          who.textContent = rv.name || 'Verified customer';
          card.appendChild(stars); card.appendChild(body); card.appendChild(who);
          host.appendChild(card);
        }
        host.removeAttribute('hidden');
      }
    } catch (e) { /* enhancement only — never break the page */ }
    try {
      document.dispatchEvent(new CustomEvent('pp:geo', { detail: geo }));
    } catch (e) { /* older Safari CustomEvent quirks — event is optional */ }
  });
})();
